import React from 'react';
import { Product } from '../../types';
import { Link } from 'react-router-dom';
import { Card, Image, Text, Group, Container } from '@mantine/core';
import SEO from '../Layout/seo';

interface CategoryDetailsProps {
	products: Product[];
	loading: boolean;
	error: string | null;
	categoryName: string;
}

const CategoryDetails: React.FC<CategoryDetailsProps> = ({
	products,
	loading,
	error,
	categoryName,
}) => {

const seoData: { [key: string]: { title: string; description: string; keywords: string; imageUrl: string; slug: string } } = {
	Bikes: {
		title: 'Classic & New Motorbikes for Sale | Scorpio Classics',
		description:
			'Browse our collection of classic and modern motorbikes. Find your perfect ride at Scorpio Classics.',
		keywords:
			'classic motorbikes, new motorcycles, vintage bikes, restored motorbikes',
		imageUrl: 'https://www.scorpioclassics.com/images/motorbikes-category.jpg',
		slug: `/categories/${categoryName}`,
	},
	'Juke Boxes': {
		title: 'Vintage Jukeboxes for Sale | Scorpio Classics',
		description:
			'Discover our selection of classic jukeboxes, perfect for adding a retro feel to any space.',
		keywords: 'vintage jukeboxes, retro music players, classic jukebox sales',
		imageUrl: 'https://www.scorpioclassics.com/images/jukeboxes-category.jpg',
		slug: `/categories/${categoryName}`,
	},
	Signs: {
		title: 'Vintage Metal Automobile Signs | Scorpio Classics',
		description:
			'Shop our collection of retro metal automobile signs, ideal for collectors and enthusiasts.',
		keywords:
			'vintage metal signs, classic car signs, retro metal collectibles',
		imageUrl: 'https://www.scorpioclassics.com/images/metal-signs-category.jpg',
		slug: `/categories/${categoryName}`,
	},
	Collectibles: {
		title: 'Vintage Memorabilia & Collectibles | Scorpio Classics',
		description:
			'Explore unique vintage memorabilia and collectibles for classic car and bike enthusiasts.',
		keywords:
			'vintage memorabilia, retro collectibles, classic vehicle souvenirs',
		imageUrl: 'https://www.scorpioclassics.com/images/memorabilia-category.jpg',
		slug: `/categories/${categoryName}`,
	},
};

// Default SEO values if category doesn't match
const categorySEO = seoData[categoryName] || {
	title: `${categoryName} | Scorpio Classics`,
	description: `Browse ${categoryName} products at Scorpio Classics.`,
	keywords: `${categoryName.toLowerCase()}, Scorpio Classics, vintage collectibles`,
	imageUrl: 'https://www.scorpioclassics.com/images/default-category.jpg',
	slug: `/categories/${categoryName}`,
};

	if (loading) {
		return <Text>Loading...</Text>;
	}

	if (error) {
		return (
			<Text
				color="red">
				{error}
			</Text>
		);
	}

	return (
		<div>
			<SEO
				title={categorySEO.title}
				description={categorySEO.description}
				keywords={categorySEO.keywords}
				imageUrl={categorySEO.imageUrl}
				slug={categorySEO.slug}
			/>
			<Container>
				<Text
					size="xl"
					mt="md">
					Products in {categoryName}
				</Text>
				<div
					style={{
						display: 'grid',
						gap: '1rem',
						gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
						marginTop: '2rem',
					}}>
					{products.map((product) => (
						<Card
							key={product.productId}
							shadow="sm"
							padding="lg"
							radius="md"
							withBorder
							style={{
								opacity: product.sold ? 0.6 : 1, // Dim sold products
							}}>
							<Card.Section>
								<Link
									to={`/categories/${categoryName}/${product.productTitle}-${product.productId}`}>
									<Image
										src={`https://api.scorpioclassics.com/uploads/${product.imagePaths[0]}`}
										alt={product.productTitle}
										height={160}
										fit="cover"
									/>
								</Link>
							</Card.Section>
							<Group
								justify="space-between"
								mt="md">
								<Text>{product.productTitle}</Text>
								<Text color="green">£{product.price.toFixed(2)}</Text>
							</Group>
							<Text
								size="sm"
								mt="xs"
								color="dimmed">
								{product.productDescription}
							</Text>
							{product.sold && (
								<Text
									color="red"
									fw={700}
									mt="sm">
									Sold
								</Text>
							)}
						</Card>
					))}
				</div>
			</Container>
		</div>
	);
};

export default CategoryDetails;
